import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ANALYTICS_ENABLED, GOATCOUNTER_ENDPOINT } from "../../config/analytics";

/**
 * GoatCounter でページビューを計測するコンポーネント。
 * 画面には何も表示しません。<Router> の内側に置いて使います。
 *
 * このサイトは SPA（ページ遷移してもブラウザの読み込みが起きない）なので、
 * 読み込み時の自動カウントを切って、ルートが変わるたびに手動でカウントを送っています。
 */

const SCRIPT_ID = "goatcounter-script";
const SCRIPT_SRC = "https://gc.zgo.at/count.js";

declare global {
  interface Window {
    goatcounter?: {
      no_onload?: boolean;
      count?: (vars: { path: string; title?: string }) => void;
    };
  }
}

const Analytics: React.FC = () => {
  const location = useLocation();

  // 計測スクリプトを一度だけ読み込む
  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;
    if (document.getElementById(SCRIPT_ID)) return;

    // 読み込み時の自動カウントを無効化（下の useEffect で自前に送るため）
    window.goatcounter = { ...window.goatcounter, no_onload: true };

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = SCRIPT_SRC;
    script.setAttribute("data-goatcounter", GOATCOUNTER_ENDPOINT);
    document.body.appendChild(script);
  }, []);

  // ページが切り替わるたびにカウントを送る
  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;

    // スクリプトの読み込みが終わる前の遷移も拾えるようにリトライする
    let attempts = 0;
    const send = () => {
      if (window.goatcounter?.count) {
        window.goatcounter.count({
          path: window.location.pathname + window.location.search,
          title: document.title,
        });
        return;
      }
      if (attempts < 20) {
        attempts += 1;
        window.setTimeout(send, 250);
      }
    };
    send();
  }, [location.pathname, location.search]);

  return null;
};

export default Analytics;
